import type { Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import { AwningType } from "building-types/settings/settingsTypesEnum";
import { BuiltinWindowId } from "building-types/windows/windowId";
import type { GeneratedWindowPlacement, GeneratedWindowPlacementDeprecated } from "building-types/windows/windowTypes";
import { levelInchesNew } from "common/utils/nominalNumbers";
import uid from "common/utils/uid";
import { getMergedWindowSettings } from "doc/elements/window/getMergedWindowSettings";
import { defaultWindowCatalog } from "doc/elements/window/windowCatalog";
import { windowDescriptorSetSize } from "doc/elements/window/windowDescriptors";
import { planarGraphAdjacentHalfEdge, planarGraphHalfEdgeLength } from "geom/planarGraph";
import { LineAnchorEnum } from "geom/polyLine2Types";
import { rangeNonInclusiveByMinStep } from "geom/range";
import { getWindowHeight } from "../../window/windowHeightTools";
import { generatedWindowPlacementDeprecatedNew } from "../../window/windowTools";
import type { OpeningPlacementFuncOptions } from "../openingPlacementFuncOptions";

export function windowPlacementFunc({
  roomStart,
  halfEdgeIndex,
  planarGraph,
  level,
  levelAttributes,
  planarGraphPerimeter,
  windowHomeSettings,
  mergeSettingsForOpeningParams,
}: OpeningPlacementFuncOptions): { windowPlacements: GeneratedWindowPlacement[] } | null {
  const wallLength = planarGraphHalfEdgeLength(planarGraph.planarGraph, halfEdgeIndex);

  const defaultWindowType = roomStart?.attributes?.windowType;
  let descriptor = defaultWindowType
    ? defaultWindowCatalog[defaultWindowType]
    : defaultWindowCatalog.A3050 || defaultWindowCatalog.A3050;

  if (descriptor.id === BuiltinWindowId.NO) {
    return null;
  }

  // adjust for ceiling height
  if (level.topOfWall < 108) {
    descriptor = windowDescriptorSetSize(
      descriptor,
      descriptor.width,
      levelInchesNew(getWindowHeight(descriptor) - 12),
    );
  }

  if (wallLength < descriptor.width + 16) {
    return null;
  }

  const { entitySettings, activeSettingsSets, settings, optionChoices } = mergeSettingsForOpeningParams;

  const { exteriorCasing, interiorCasing } = getMergedWindowSettings({
    homeSettings: windowHomeSettings,
    activeSettingsSets,
    entitySettings,
    optionChoices,
    descriptor,
    settings,
  });

  const oppHalfEdge = planarGraphAdjacentHalfEdge(halfEdgeIndex);
  const streetOrientation = planarGraphPerimeter.coveredPerimeterStreetOrientations[oppHalfEdge];

  // default spacing
  let minWindowSpacing = descriptor.width + 38;

  if (roomStart?.type === RoomType.GARAGE || roomStart?.type === RoomType.HALL) {
    minWindowSpacing = descriptor.width + 48;
  }

  const paramAnchor = LineAnchorEnum.START;

  if (roomStart?.type === RoomType.BAY_WINDOW) {
    const windowPlacements: GeneratedWindowPlacementDeprecated[] = [];

    minWindowSpacing = descriptor.width + 12;
    const normalizedMinWindowSpacing = minWindowSpacing / wallLength;
    for (const t of rangeNonInclusiveByMinStep(0, 1.0, normalizedMinWindowSpacing)) {
      windowPlacements.push(
        generatedWindowPlacementDeprecatedNew({
          id: uid(),
          descriptor,
          frontRoom: roomStart,
          halfEdgeIndex,
          param: t,
          paramAnchor,
          exteriorCasing,
          interiorCasing,
          awningSettings: { awningType: AwningType.DISABLED },
          paramNormalized: true,
          headHeight: levelAttributes.windowHeadHeight,
          streetOrientation,
        }),
      );
    }
    return { windowPlacements };
  }

  const MULTI_WINDOW_THRESHOLD = 2.5;
  if (wallLength > descriptor.width * MULTI_WINDOW_THRESHOLD) {
    const windowPlacements: GeneratedWindowPlacementDeprecated[] = [];
    const normalizedMinWindowSpacing = minWindowSpacing / wallLength;

    for (const t of rangeNonInclusiveByMinStep(0, 1.0, normalizedMinWindowSpacing)) {
      windowPlacements.push(
        generatedWindowPlacementDeprecatedNew({
          id: uid(),
          descriptor,
          frontRoom: roomStart as Room,
          halfEdgeIndex,
          param: t,
          paramAnchor,
          exteriorCasing,
          interiorCasing,
          paramNormalized: true,
          headHeight: levelAttributes.windowHeadHeight,
          awningSettings: { awningType: AwningType.DISABLED },
          streetOrientation,
        }),
      );
    }
    return { windowPlacements };
  } else {
    const windowPlacements = [
      generatedWindowPlacementDeprecatedNew({
        id: uid(),
        descriptor,
        frontRoom: roomStart as Room,
        halfEdgeIndex,
        param: 0.5,
        paramAnchor,
        exteriorCasing,
        interiorCasing,
        paramNormalized: true,
        headHeight: levelAttributes.windowHeadHeight,
        awningSettings: { awningType: AwningType.DISABLED },
        streetOrientation,
      }),
    ];
    return { windowPlacements };
  }
}
